import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { GetProctoringConfigurationByIds } from '@edtech-ui/core/models/proctoring-dashboard-models/get-proctoring-config-by-id';
import { UpdateProctoringConfigurationPayload } from '@edtech-ui/core/models/proctoring-dashboard-models/update-proctoring-config';
import { ProctoringService } from '@edtech-ui/core/services/proctoring-dashboard/proctoring.service';
import { allowedDevicesBrowsersProto } from '@edtech-ui/core/utils/proctoring-dashboard/allowed devises.validators';
import { ProctoringConfigValidationUtils } from '@edtech-ui/core/utils/proctoring-dashboard/proctoring-config';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { DefultsRulesDialogBoxComponent } from '../defults-rules-dialog-box/defults-rules-dialog-box.component';
import { MatDialog } from '@angular/material/dialog';
import { NonDefaultsDialogBoxComponent } from '../non-defaults-dialog-box/non-defaults-dialog-box.component';

@Component({
	selector: 'app-proctoring-configuration-detail-page',
	standalone: true,
	imports: [
		CommonModule, FormsModule, RouterModule, MatProgressSpinnerModule, ReactiveFormsModule, HasPermissionDirective
	],
	templateUrl: './proctoring-configuration-detail-page.component.html',
	styleUrl: './proctoring-configuration-detail-page.component.scss'
})
export class ProctoringConfigurationDetailPageComponent implements OnInit, OnDestroy {

	isEditing = false;
	isLoading = false;
	isSaving = false;
	subscription = new Subscription();
	configId = '';
	configEditForm: FormGroup;
	currentPage = 1;
	pageSize = 10;
	search = '';
	configData: GetProctoringConfigurationByIds | null = null;
	companyId = '';
	PermissionForStudents = PermissionForStudents;


	constructor(
		private route: ActivatedRoute,
		private proctoringService: ProctoringService,
		private toastr: ToastrService,
		private dialog: MatDialog
	) {
		this.configEditForm = new FormGroup(({
			name: new FormControl('', [Validators.required, ProctoringConfigValidationUtils.configurationNameValidator()]),
			description: new FormControl('', [ProctoringConfigValidationUtils.proctoringDescriptionValidator()]),
			isEnabled: new FormControl(),
			isDefault: new FormControl(false),
			allowedBrowsers: new FormControl(['Chrome', 'Edge'], [allowedDevicesBrowsersProto()]),
			allowedDevices: new FormControl(['Desktop', 'Laptop'], [allowedDevicesBrowsersProto()]),
			requireCamera: new FormControl(false),
			requireMicrophone: new FormControl(false),
			requireScreenShare: new FormControl(false),
			requireFullscreen: new FormControl(false),
			recordCamera: new FormControl(false),
			recordScreen: new FormControl(false),
			recordAudio: new FormControl(false),
			maxMedium: new FormControl(1, [Validators.required, ProctoringConfigValidationUtils.proctoringThresholdValidator()]),
			maxHigh: new FormControl(1, [Validators.required, ProctoringConfigValidationUtils.proctoringThresholdValidator()]),
			maxCritical: new FormControl(1, [Validators.required, ProctoringConfigValidationUtils.proctoringThresholdValidator()])
		}));
		this.configEditForm.disable();
	}


	ngOnInit(): void {
		this.companyId = localStorage.getItem('company_id') || '';
		this.route.queryParamMap.subscribe(params => {
			this.isEditing = params.get('edit') === 'true';
		});

		this.configId = this.route.snapshot.paramMap.get('configId') || '';
		if (this.configId) {
			this.getProctoringConfigById();
		}
	}



	getProctoringConfigById() {
		this.isLoading = true;

		this.subscription.add(
			this.proctoringService.getProctoringConfigById(this.configId).pipe(
				tap(response => {
					if (response.success) {
						this.configData = response.data;
						this.configEditForm.patchValue({
							...response.data,
							screenMonitoring: false,
							screenshotCapture: false
						});
						this.configEditForm.disable();
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}


	updateProctoringConfigById() {

		if (this.configEditForm.invalid) {
			this.configEditForm.markAllAsTouched();
			return;
		}
		const formValue = this.configEditForm.getRawValue();

		const payload: UpdateProctoringConfigurationPayload = {
			companyId: this.configData?.companyId ?? '',
			name: formValue.name ?? '',
			description: formValue.description ?? '',
			isEnabled: formValue.isEnabled ?? false,
			isDefault: formValue.isDefault ?? false,
			allowedBrowsers: formValue.allowedBrowsers ?? [],
			allowedDevices: formValue.allowedDevices ?? [],
			requireCamera: formValue.requireCamera ?? false,
			requireMicrophone: formValue.requireMicrophone ?? false,
			requireScreenShare: formValue.requireScreenShare ?? false,
			requireFullscreen: formValue.requireFullscreen ?? false,
			recordCamera: formValue.recordCamera ?? false,
			recordScreen: formValue.recordScreen ?? false,
			recordAudio: formValue.recordAudio ?? false,
			maxMedium: formValue.maxMedium ?? 1,
			maxHigh: formValue.maxHigh ?? 1,
			maxCritical: formValue.maxCritical ?? 1
		};
		this.isSaving = true;
		this.subscription.add(
			this.proctoringService.updateProctoringConfigById(this.configId, payload).pipe(
				tap(response => {
					if (response.success) {
						this.toastr.success(response.message);
						this.getProctoringConfigById();
						this.configEditForm.disable();
						this.isEditing = false;
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isSaving = false;
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}


	toggleEdit() {
		this.isEditing = !this.isEditing;

		if (this.isEditing) {
			this.configEditForm.enable();
		} else {
			this.configEditForm.disable();

			if (this.configData) {
				this.configEditForm.patchValue(this.configData);
			}
		}
	}



	browsers: string[] = ['Chrome', 'Edge', 'Firefox', 'Safari'];

	devices: string[] = ['Desktop', 'Laptop', 'Tablet', 'Mobile'];


	toggleSwitch(control: string) {

		if (!this.isEditing) {
			return;
		}

		const value = this.configEditForm.get(control)?.value;

		this.configEditForm.patchValue({
			[control]: !value
		});
	}


	toggleBrowser(browser: string) {
		if (!this.isEditing) {
			return;
		}
		const selected = [...(this.configEditForm.getRawValue().allowedBrowsers || [])];
		const index = selected.indexOf(browser);

		if (index > -1) {
			selected.splice(index, 1);
		} else {
			selected.push(browser);
		}

		this.configEditForm.patchValue({
			allowedBrowsers: selected
		});
		this.configEditForm.get('allowedBrowsers')?.markAsTouched();
		this.configEditForm.get('allowedBrowsers')?.updateValueAndValidity();
	}

	isBrowserSelected(browser: string) {
		return this.configEditForm.getRawValue().allowedBrowsers?.includes(browser);
	}


	toggleChildToggle(parent: string, child: string) {
		if (!this.configEditForm.value[parent]) {
			return;
		}
		this.toggleSwitch(child);
	}


	toggleDevice(device: string) {
		if (!this.isEditing) {
			return;
		}
		const devices = [...(this.configEditForm.getRawValue().allowedDevices || [])];
		const index = devices.indexOf(device);

		if (index > -1) {
			devices.splice(index, 1);
		} else {
			devices.push(device);
		}

		this.configEditForm.patchValue({
			allowedDevices: devices
		});
		this.configEditForm.get('allowedDevices')?.markAsTouched();
		this.configEditForm.get('allowedDevices')?.updateValueAndValidity();
	}

	isDeviceSelected(device: string): boolean {
		return this.configEditForm.getRawValue().allowedDevices?.includes(device);
	}


	cancelEdit() {
		this.isEditing = false;
		this.configEditForm.disable();
		this.getProctoringConfigById();
	}


	openNonDefaultDialogBox(){
		this.dialog.open(NonDefaultsDialogBoxComponent,{
			data:{
				type:'configurations'
			}
		})
	}


	openDefaultsDialogBox(): void {
		if (!this.isEditing) {
			return;
		}

		const currentValue = this.configEditForm.get('isDefault')?.value;
		if (currentValue) {
			this.openNonDefaultDialogBox()
			return;
		}

		const dialogRef = this.dialog.open(DefultsRulesDialogBoxComponent, {
			width: '500px',
			disableClose: true,
			data:{
				  type: 'configuration'
			}
		});

		dialogRef.afterClosed().subscribe((result: boolean) => {
			if (result) {
				this.configEditForm.patchValue({
					isDefault: true
				});
			}
		});
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}
